import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import {
  Search,
  GraduationCap,
  CheckCircle,
  Clock,
  Plus,
  Eye,
  Award,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Inbox,
  Loader2,
  Users,
  XCircle,
  FileText
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { admissionsAPI, adminAPI } from '../../services/api';

const APPLICATION_STATUSES = [
  'Assigned', 'Docs Pending', 'Docs Verified', 'Submitted',
  'Under Review', 'Offer Received', 'Accepted', 'Visa Processing',
  'Completed', 'Rejected'
];

const STATUS_COLORS = {
  'Assigned': 'bg-blue-100 text-blue-700',
  'Docs Pending': 'bg-amber-100 text-amber-700',
  'Docs Verified': 'bg-cyan-100 text-cyan-700',
  'Submitted': 'bg-indigo-100 text-indigo-700',
  'Under Review': 'bg-purple-100 text-purple-700',
  'Offer Received': 'bg-emerald-100 text-emerald-700',
  'Accepted': 'bg-green-100 text-green-700',
  'Visa Processing': 'bg-orange-100 text-orange-700',
  'Completed': 'bg-green-200 text-green-800',
  'Rejected': 'bg-red-100 text-red-700'
};

export default function AdminAdmissions() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [universityFilter, setUniversityFilter] = useState('');
  const [intakeFilter, setIntakeFilter] = useState('');

  // Assign modal
  const [assignOpen, setAssignOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [agents, setAgents] = useState([]);
  const [students, setStudents] = useState([]);
  const [assignData, setAssignData] = useState({
    studentId: '',
    agentId: '',
    universityName: '',
    programName: '',
    programLevel: '',
    intake: ''
  });

  const fetchApplications = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (universityFilter) params.university = universityFilter;
      if (intakeFilter) params.intake = intakeFilter;
      if (searchTerm) params.search = searchTerm;

      const result = await admissionsAPI.getAdminList(params);
      setApplications(result.data || []);
      setPagination(result.pagination || { total: 0, page: 1, limit: 20, totalPages: 0 });
    } catch (err) {
      console.error('Failed to fetch applications:', err);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, universityFilter, intakeFilter, searchTerm]);

  useEffect(() => {
    fetchApplications(1);
  }, [fetchApplications]);

  const fetchAssignData = async () => {
    try {
      const [agentsRes, studentsRes] = await Promise.all([
        adminAPI.getAgents({ limit: 200 }),
        adminAPI.getStudents({ limit: 200 })
      ]);
      setAgents(agentsRes.data || agentsRes.agents || []);
      setStudents(studentsRes.data || studentsRes.students || []);
    } catch (err) {
      console.error('Failed to fetch assign data:', err);
    }
  };

  const handleAssignOpen = () => {
    fetchAssignData();
    setAssignData({ studentId: '', agentId: '', universityName: '', programName: '', programLevel: '', intake: '' });
    setAssignOpen(true);
  };

  const handleAssign = async () => {
    const { studentId, agentId, universityName, programName, intake } = assignData;
    if (!studentId || !agentId || !universityName || !programName || !intake) {
      toast.error('Please fill in all required fields');
      return;
    }
    setAssigning(true);
    try {
      await admissionsAPI.assign(assignData);
      toast.success('Application assigned successfully');
      setAssignOpen(false);
      fetchApplications(1);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to assign application');
    } finally {
      setAssigning(false);
    }
  };

  // Stats from current page data
  const totalApps = pagination.total;
  const activeCount = applications.filter(a => !['Completed', 'Rejected'].includes(a.status)).length;
  const offerCount = applications.filter(a => a.status === 'Offer Received').length;
  const completedCount = applications.filter(a => a.status === 'Completed').length;
  const rejectedCount = applications.filter(a => a.status === 'Rejected').length;

  const getInitials = (obj) => {
    if (!obj || !obj.firstName) return '??';
    return `${obj.firstName?.[0] || ''}${obj.lastName?.[0] || ''}`;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-gray-900">Admissions Management</h1>
            <p className="text-gray-600">View and manage all university applications across agents</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => fetchApplications(pagination.page)}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={handleAssignOpen} className="bg-[#F2575F] hover:bg-[#E04850]">
              <Plus className="w-4 h-4 mr-2" />
              Assign Application
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="p-4 bg-white border-stone-200/80">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{totalApps}</p>
                <p className="text-sm text-gray-500">Total</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border-stone-200/80">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
                <p className="text-sm text-gray-500">Active</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border-stone-200/80">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{offerCount}</p>
                <p className="text-sm text-gray-500">Offers</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border-stone-200/80">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{completedCount}</p>
                <p className="text-sm text-gray-500">Completed</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border-stone-200/80">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{rejectedCount}</p>
                <p className="text-sm text-gray-500">Rejected</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4 bg-white border-stone-200/80">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by student name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={(val) => setStatusFilter(val === 'all' ? '' : val)}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {APPLICATION_STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="University..."
              value={universityFilter}
              onChange={(e) => setUniversityFilter(e.target.value)}
              className="w-full md:w-48"
            />
            <Input
              placeholder="Intake..."
              value={intakeFilter}
              onChange={(e) => setIntakeFilter(e.target.value)}
              className="w-full md:w-36"
            />
          </div>
        </Card>

        {/* Table */}
        {loading ? (
          <Card className="p-12 bg-white border-stone-200/80 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F2575F]" />
          </Card>
        ) : applications.length === 0 ? (
          <Card className="p-12 bg-white border-stone-200/80">
            <div className="flex flex-col items-center justify-center text-gray-500">
              <Inbox className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-lg font-medium">No Applications Found</p>
              <p className="text-sm">Try adjusting your search or filters</p>
            </div>
          </Card>
        ) : (
          <Card className="bg-white border-stone-200/80 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50/80 border-b border-stone-200">
                  <tr>
                    <th className="text-left py-3.5 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Student</th>
                    <th className="text-left py-3.5 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">University</th>
                    <th className="text-left py-3.5 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Program</th>
                    <th className="text-left py-3.5 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Intake</th>
                    <th className="text-left py-3.5 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Agent</th>
                    <th className="text-left py-3.5 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-left py-3.5 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Assigned By</th>
                    <th className="text-left py-3.5 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Updated</th>
                    <th className="text-right py-3.5 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {applications.map((app, index) => (
                    <motion.tr
                      key={app.applicationId}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.02 }}
                      className="hover:bg-stone-50/80 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/admissions/${app.applicationId}`)}
                    >
                      <td className="py-3.5 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-semibold text-xs">
                            {getInitials(app.student)}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">
                              {app.student?.firstName} {app.student?.lastName}
                            </p>
                            <p className="text-xs text-gray-500">{app.student?.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3.5 px-6">
                        <div className="flex items-center gap-2">
                          <GraduationCap className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <span className="text-sm text-gray-900">{app.universityName}</span>
                        </div>
                      </td>
                      <td className="py-3.5 px-6 text-sm text-gray-600">{app.programName}</td>
                      <td className="py-3.5 px-6 text-sm text-gray-600">{app.intake}</td>
                      <td className="py-3.5 px-6">
                        <div className="flex items-center gap-2">
                          <Users className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-sm text-gray-700">
                            {app.agent?.firstName} {app.agent?.lastName}
                          </span>
                        </div>
                      </td>
                      <td className="py-3.5 px-6">
                        <Badge className={STATUS_COLORS[app.status] || 'bg-gray-100 text-gray-700'}>
                          {app.status}
                        </Badge>
                      </td>
                      <td className="py-3.5 px-6">
                        <Badge variant="outline" className="text-xs capitalize">{app.assignedBy}</Badge>
                      </td>
                      <td className="py-3.5 px-6 text-sm text-gray-500">
                        {new Date(app.updatedAt).toLocaleDateString()}
                      </td>
                      <td className="py-3.5 px-6 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/admissions/${app.applicationId}`);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-stone-100">
                <p className="text-sm text-gray-500">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page <= 1}
                    onClick={() => fetchApplications(pagination.page - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-600">Page {pagination.page} of {pagination.totalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => fetchApplications(pagination.page + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Assign Application Dialog */}
        <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-blue-600" />
                Assign University Application
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>Student *</Label>
                <Select value={assignData.studentId} onValueChange={(val) => setAssignData(prev => ({ ...prev, studentId: val }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select a student" />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map(s => (
                      <SelectItem key={s.studentId || s._id} value={s.studentId || s._id}>
                        {s.firstName || s.user?.firstName} {s.lastName || s.user?.lastName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Agent *</Label>
                <Select value={assignData.agentId} onValueChange={(val) => setAssignData(prev => ({ ...prev, agentId: val }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select an agent" />
                  </SelectTrigger>
                  <SelectContent>
                    {agents.map(a => (
                      <SelectItem key={a.userId || a._id} value={a.userId || a._id}>
                        {a.firstName} {a.lastName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>University Name *</Label>
                <Input
                  value={assignData.universityName}
                  onChange={(e) => setAssignData(prev => ({ ...prev, universityName: e.target.value }))}
                  placeholder="e.g. University of Toronto"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Program Name *</Label>
                <Input
                  value={assignData.programName}
                  onChange={(e) => setAssignData(prev => ({ ...prev, programName: e.target.value }))}
                  placeholder="e.g. Computer Science"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Program Level</Label>
                <Select value={assignData.programLevel} onValueChange={(val) => setAssignData(prev => ({ ...prev, programLevel: val }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select level (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Undergraduate Program">Undergraduate</SelectItem>
                    <SelectItem value="Graduate Program">Graduate</SelectItem>
                    <SelectItem value="Postgraduate Program">Postgraduate</SelectItem>
                    <SelectItem value="Diploma">Diploma</SelectItem>
                    <SelectItem value="Certificate">Certificate</SelectItem>
                    <SelectItem value="Doctoral Program">Doctoral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Intake *</Label>
                <Input
                  value={assignData.intake}
                  onChange={(e) => setAssignData(prev => ({ ...prev, intake: e.target.value }))}
                  placeholder="e.g. Fall 2025"
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
                <Button onClick={handleAssign} disabled={assigning} className="bg-[#F2575F] hover:bg-[#E04850]">
                  {assigning && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Assign Application
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
